import { key, MATERIALS, type Building, type Cell, type CellRole, type Material } from './city';

/** Purpose-built industrial envelopes, authored in the same damageable cells. */
export function industrialBuilding(b: Building, levels: number): Building {
  const cells = new Map<number, Cell>();
  const put = (x: number, y: number, z: number, role: CellRole, material: Material, color: number) => {
    const k = key(x, y, z);
    cells.set(k, {
      id: b.id * 1e6 + k,
      x,
      y,
      z,
      role,
      material,
      color,
      hp: MATERIALS[material].strength,
      root: y === 0,
    });
  };
  if (b.kind === 'grain-silo') {
    const radius = Math.min(b.w * 0.21, b.d * 0.36),
      height = Math.max(9, Math.min(24, levels * 3 + 3)),
      cone = Math.ceil(radius);
    b.roofLevel = height;
    b.h = height + cone + 1;
    for (const cx of [b.w * 0.25, b.w * 0.75])
      for (let z = 0; z < b.d; z++)
        for (let x = 0; x < b.w; x++) {
          const r = Math.hypot(x + 0.5 - cx, z + 0.5 - b.d * 0.5);
          if (r > radius) continue;
          put(x, 0, z, 'slab', 'concrete', 0xa9aaa0);
          if (r > radius - 1.15)
            for (let y = 1; y < height; y++)
              put(x, y, z, y % 4 === 0 ? 'beam' : 'wall', 'metal', y % 4 === 0 ? 0x788e90 : 0xc1c8bf);
          for (let y = height; y <= height + cone; y++) {
            if (r <= radius * (1 - (y - height) / (cone + 0.25))) put(x, y, z, 'roof', 'metal', 0x889fa0);
          }
        }
    // A grounded service connector ties both storage bins into one assembly.
    for (let x = Math.floor(b.w * 0.25); x <= Math.ceil(b.w * 0.75); x++)
      for (let z = Math.floor(b.d / 2) - 1; z <= Math.floor(b.d / 2); z++)
        put(x, 0, z, 'slab', 'concrete', 0xa9aaa0);
  } else {
    const wall = 5,
      arch = Math.max(3, Math.min(6, Math.ceil(b.w * 0.22)));
    b.roofLevel = wall;
    b.h = wall + arch + 1;
    for (let z = 0; z < b.d; z++)
      for (let x = 0; x < b.w; x++) {
        const top = wall + Math.floor(arch * Math.sqrt(Math.max(0, 1 - ((2 * (x + 0.5)) / b.w - 1) ** 2)));
        put(x, 0, z, 'slab', 'concrete', 0xb0b6ae);
        const outer = x === 0 || x === b.w - 1 || z === 0 || z === b.d - 1;
        if (outer)
          for (let y = 1; y < top; y++) {
            // Full-height aircraft doorway. The barrel roof is carried by its side
            // walls and repeated end-frame piers, with a clear interior volume.
            if (z === 0 && x >= 2 && x < b.w - 2 && y < wall) continue;
            put(x, y, z, x <= 1 || x >= b.w - 2 ? 'column' : 'wall', 'metal', z === 0 ? b.accent : b.color);
          }
        // Occupied stepped vault with no disconnected slivers or hidden solid box.
        const neighbour = (xx: number) =>
          wall + Math.floor(arch * Math.sqrt(Math.max(0, 1 - ((2 * (xx + 0.5)) / b.w - 1) ** 2)));
        const bottom = Math.min(top, neighbour(Math.max(0, x - 1)), neighbour(Math.min(b.w - 1, x + 1)));
        for (let y = bottom; y <= top; y++) put(x, y, z, 'roof', 'metal', z % 4 === 0 ? 0x778c8d : 0xc2ccca);
      }
  }
  b.cells = [...cells.values()];
  return b;
}
